import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from '../styles/FeaturedListings.module.css';
import { withShopifyWidth } from '../utils/shopifyImage';
import { fetchJsonWithBusyRetry } from '../utils/fetchJsonWithBusyRetry';
import { slugify } from '../data/catalog';
import SensitiveThumbnail from './SensitiveThumbnail';

// Fila de anuncios destacados (pagados por vendedores del marketplace,
// server/services/featuredListingsService.js) -- aparte del listado
// organico, con su propia etiqueta "Anuncio" visible en cada tarjeta
// (mismo criterio de transparencia que usa Google con sus resultados
// patrocinados). Las posiciones sin vender se rellenan en el backend con
// nuestros propios chollos de mas descuento como gancho mientras no hay
// vendedores pagando -- esos NO llevan la etiqueta "Anuncio" (no lo son),
// llevan "Top descuento" en su lugar (deal.isSponsored distingue ambos).
//
const SKELETON_SLOTS = [0, 1, 2, 3, 4];

const FeaturedListings = ({ excludeIds = [], onLoadedIds, label = 'Destacados' }) => {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        const controller = new AbortController();
        const params = new URLSearchParams({ placement: 'top' });
        if (excludeIds.length > 0) params.set('exclude', excludeIds.join(','));
        // Reintenta en silencio ante un 503 (SERVER_BUSY, ver
        // publicReadProtection.js) antes de rendirse -- sin esto, un 503
        // transitorio (cache fria, pico de trafico) hacia que la fila entera
        // desaparezca sin aviso ni reintento (deals.length===0 -> return null),
        // indistinguible de "no hay destacados de verdad".
        fetchJsonWithBusyRetry(`/api/deals/featured?${params.toString()}`, { signal: controller.signal, maxRetries: 2 })
            .then((data) => {
                if (active) {
                    const loaded = data.deals || [];
                    setDeals(loaded);
                    if (onLoadedIds) {
                        onLoadedIds(loaded.map((deal) => deal.id));
                    }
                }
            })
            .catch(() => {})
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [excludeIds.join(',')]);

    // Mientras esta cargando reserva el mismo espacio que ocupara la fila
    // real (esqueleto, no null) -- antes no se pintaba nada hasta tener la
    // respuesta de /api/deals/featured, y la fila entera (5 tarjetas)
    // aparecia de golpe empujando todo el contenido de debajo: el mayor
    // causante de CLS de la home (mismo patron que el esqueleto del hero,
    // ver Home.module.css).
    if (loading) {
        return (
            <section className={styles.section} aria-hidden="true">
                <p className={styles.eyebrow}>{label}</p>
                <div className={styles.grid}>
                    {SKELETON_SLOTS.map((key) => (
                        <div className={styles.card} key={key}>
                            <span className={`${styles.image} ${styles.imageSkeleton}`} />
                            <div className={styles.info}>
                                <span className={styles.storeSkeleton} />
                                <span className={styles.nameSkeleton} />
                                <span className={styles.priceSkeleton} />
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    if (deals.length === 0) {
        return null;
    }

    return (
        <section className={styles.section} aria-label="Anuncios destacados">
            <p className={styles.eyebrow}>{label}</p>
            <div className={styles.grid}>
                {deals.map((deal) => (
                    <Link to={`/ofertas/${deal.id}`} state={{ deal }} key={deal.id} className={styles.card}>
                        <span className={deal.isSponsored ? styles.adBadge : styles.hookBadge}>
                            {deal.isSponsored ? 'Anuncio' : 'Top descuento'}
                        </span>
                        <div className={styles.imageWrap}>
                            <SensitiveThumbnail
                                isSensitive={slugify(deal.category) === 'erotico-adultos'}
                                src={withShopifyWidth(deal.imageUrl, 350)}
                                alt=""
                                className={styles.image}
                                loading="lazy"
                                decoding="async"
                            />
                            {Number(deal.discount || 0) > 0 && (
                                <span className={styles.discountBadge} aria-hidden="true">-{Math.round(deal.discount)}%</span>
                            )}
                        </div>
                        <div className={styles.info}>
                            <span className={styles.store}>{deal.store}</span>
                            <strong className={styles.name}>{deal.name}</strong>
                            <span className={styles.priceRow}>
                                <span className={styles.price}>{Number(deal.price || 0).toFixed(2)}€</span>
                                {Number(deal.originalPrice || 0) > Number(deal.price || 0) && (
                                    <span className={styles.originalPrice}>{Number(deal.originalPrice).toFixed(2)}€</span>
                                )}
                            </span>
                        </div>
                    </Link>
                ))}
            </div>
        </section>
    );
};

export default FeaturedListings;
